import React, { useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Button, 
  Grid, 
  Paper, 
  Typography, 
  IconButton, 
  Modal, 
  Alert, 
  TextField,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  Fab,
  Divider,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Skeleton
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelIcon from '@mui/icons-material/Cancel';
import MapIcon from '@mui/icons-material/Map';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import useServices from '../../services/useServices';
import moment from 'moment';
import { getSessionData } from '../../utils/utils';
import TrackingComponent from '../../components/TrackingComponent';
import RouteMap from '../../components/RouteMap';
import useMediaQuery from '@mui/material/useMediaQuery';

const CALL_STATUS_MAP = {
  "Unassigned": 0,
  "Assigned": 1,
  "Accepted": 2,
  "Enroute": 3,
  "Arrived": 4,
  "Hooked": 5,
  "Dropped": 6,
  "Cleared": 7
};

const PHASE_MAP = {
  "enroute_to_pickup": [1, 2, 3],
  "enroute_to_dropoff": [4, 5, 6],
  "pickup_to_drop": [0]
};

const steps = [
  {
    label: "Request Received",
    status: "requestTime",
    class: "requested",
    description: null
  },
  {
    label: "Driver Assigned",
    status: "acceptedTime",
    class: "accepted",
    description: null
  },
  {
    label: "Driver Enroute",
    status: "enrouteTime",
    class: "onroute",
    description: null
  },
  {
    label: "Driver Arrived",
    status: "arrivedTime",
    class: "arrived",
    description: null
  }
];

const TrackOrder = () => {
  const navigate = useNavigate();
  const { get, put } = useServices();
  const [showTrackingDetailsID, setShowTrackingDetailsID] = useState();
  const [cancelReason, setCancelReason] = useState("");
  const [isOpenCancelWindow, setIsOpenCancelWindow] = useState(false);
  const [requestDetails, setRequestDetails] = useState({});
  const [initialRespData, setInitialRespData] = useState([]);
  const [trackingData, setTrackingData] = useState(JSON.parse(JSON.stringify(steps)));
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [anchorEl, setAnchorEl] = useState(null);
  const cancelBtnRef = useRef(null);
  const [invoiceToCancel, setInvoiceToCancel] = useState("");
  const ourRequest = useRef(null);
  const [modalStyle, setModalStyle] = useState({});
  const [openRouteMap, setRouteMap] = useState(false);
  const [mapType, setMapType] = useState('enroute');
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [openTrackingModal, setOpenTrackingModal] = useState(false);
  const [openActionDrawer, setOpenActionDrawer] = useState(false);
  const [selectedInvoiceForActions, setSelectedInvoiceForActions] = useState(null);
  
  // Responsive breakpoints
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');
  const isSmallMobile = useMediaQuery('(max-width:400px)');

  const gotoHome = () => { navigate("/newrequest") };
  const handleOpen = () => setIsOpenCancelWindow(true);
  const handleClose = () => setIsOpenCancelWindow(false);
  const serviceOrders = getSessionData('serviceOrders');
  const serviceRequestNumbers = serviceOrders ? JSON.parse(serviceOrders) : [];
  
  const clearFromTrack = async () => { 
    setIsRefreshing(true);
    try {
      await fetchInitialData();
      setAlertMessage("Orders refreshed successfully!");
      setAlertSeverity("success");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 2000);
    } catch (error) {
      setAlertMessage("Failed to refresh orders");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 2000);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  const handlePopoverOpen = () => { if (!cancelReason) { setAnchorEl(cancelBtnRef.current); } };
  const handlePopoverClose = () => { setAnchorEl(null); };
  const open = Boolean(anchorEl);
  const [deliveryPhase, setDeliveryPhase] = useState('enroute_to_pickup');

  const showTrackingDetails = (id) => {
    setTrackingData(JSON.parse(JSON.stringify(steps)));
    if (id === showTrackingDetailsID) {
      setShowTrackingDetailsID(null);
      setRequestDetails({});
      setIsLoading(false);
      setOpenTrackingModal(false);
    } else {
      setIsLoading(true);
      setRequestDetails(initialRespData[id]);
      setShowTrackingDetailsID(id);
      setOpenTrackingModal(true);
    }
  };

  const setTrackingDetailsData = () => {
    if (requestDetails.callStatus !== 'Cancel') {
      trackingData.map(item => {
        const dateTime = requestDetails[item.status];
        item.description = dateTime ? moment(dateTime).format('HH:mm:ss MM/DD/YYYY') : dateTime;
        return item;
      });
      setTrackingData(JSON.parse(JSON.stringify(trackingData)));
    }
    setIsLoading(false);
  };

  const fetchInitialData = async () => {
    try {
      const userID = getSessionData("userID");
      const ordersData = await get(`ars/directCall/` + userID);
      const respData = ordersData.data?.resp || [];
      const newDataArray = [];
      newDataArray.push(...(respData || []));
      setInitialRespData(newDataArray);
    } catch (error) {
      setAlertMessage("No active orders found");
      setAlertSeverity("success");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
    }
  };

  useEffect(() => {
    const handlePopState = (event) => {
      if (openTrackingModal) {
        setOpenTrackingModal(false);
      } else {
        navigate(-1);
      }
    };
  
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [openTrackingModal]);
  
  useEffect(() => {
    if (openTrackingModal) {
      window.history.pushState({ openTrackingModal: true }, '');
    }
  }, [openTrackingModal]);

  useEffect(() => {
  if (openRouteMap) {
    // Trigger map resize after modal is fully open
    setTimeout(() => {
      if (window.google && window.google.maps) {
        window.google.maps.event.trigger(window, 'resize');
      }
    }, 300);
  }
}, [openRouteMap]);

  const cancelInvoice = async () => {
    try {
      const userID = getSessionData("userID");
      if (userID) {
        const requestBody = {
          "invoiceId": invoiceToCancel,
          "callStatus": "Cancel",
          "reason": cancelReason,
          statusValue: "Edit"
        };
        const res = await put(`ars/directCall`, requestBody);
        handleClose();
        if (res.data.statusCode === 200) {
          setAlertMessage("Call has been cancelled successfully!");
          setAlertSeverity("success");
          fetchInitialData();
        } else {
          setAlertMessage("Failed to cancel the call!");
          setAlertSeverity("error");
        }
        setCancelReason("");
        setShowAlert(true);
        setTimeout(() => {
          setShowAlert(false);
        }, 3000);
      } else {
        throw 'user not valid';
      }
    } catch (error) {
      console.log(error);
      setAlertMessage("Error cancelling call");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    const userID = getSessionData("userID");
    if (!userID) {
      // navigate("/unauthorizedUser")
    }
  }, []);

  useEffect(() => {
    setTrackingDetailsData();
  }, [requestDetails]);

  useEffect(() => {
    const updateModalStyle = () => {
      const baseStyles = {
        position: 'absolute',
        overflow: 'auto',
        backgroundColor: '#FFF',
        boxSizing: 'border-box',
        border: isMobile ? 'none' : '4px solid #f21c0c',
        borderRadius: isMobile ? '12px 12px 0 0' : '8px',
      };

      if (isMobile) {
        setModalStyle({
          ...baseStyles,
          top: '10%',
          left: '0%',
          width: '100%',
          height: '80%',
          maxHeight: '80vh',
        });
      } else if (isTablet) {
        setModalStyle({
          ...baseStyles,
          top: '5%',
          left: '5%',
          width: '90%',
          height: '90%',
        });
      } else {
        setModalStyle({
          ...baseStyles,
          top: '10%',
          left: '10%',
          width: '80%',
          height: '80%',
        });
      }
    };

    updateModalStyle();
    window.addEventListener('resize', updateModalStyle);
    return () => {
      window.removeEventListener('resize', updateModalStyle);
    };
  }, [isMobile, isTablet]);

  const handleRouteMapOpen = (index) => {
    const selectedItem = initialRespData[index];
    setRouteCoordinates([]);
    const parseCoordinates = (coordStr) => {
      if (!coordStr) return null;
      const [lat, lng] = coordStr.split(',').map(parseFloat);
      if (!lat || !lng) return null;
      return { lat, lng };
    };
    const driverCurrentGps = parseCoordinates(selectedItem.driverCurrentGps);
    const pickupCoordinate = parseCoordinates(selectedItem.pickupCoordinate);
    const dropoffCoordinate = parseCoordinates(selectedItem.dropoffCoordinate);
    const data = [
      {
        addressType: 'Pickup',
        address: selectedItem.pickup || '',
        coordinates: pickupCoordinate,
        eta: selectedItem.etaPick
      },
      {
        addressType: 'Truck',
        address: selectedItem.truckCurrentAddress || '',
        coordinates: driverCurrentGps
      },
      {
        addressType: 'Drop',
        address: selectedItem.dropoff || '',
        coordinates: dropoffCoordinate,
        eta: selectedItem.etaDrop
      }
    ];
    setRouteCoordinates(data);
    setRouteMap(true);
  };

  const handleInvoiceActions = (item, index) => {
    setSelectedInvoiceForActions({ item, index });
    setOpenActionDrawer(true);
  };

  const getStatusColor = (callStatus) => {
    switch (callStatus) {
      case 'Assigned': return '#2196f3';
      case 'Accepted': return '#4caf50';
      case 'Enroute': return '#ff9800';
      case 'Arrived': return '#9c27b0';
      case 'Cancel': return '#f44336';
      default: return '#757575';
    }
  };

  const formatElapsedTime = (elapsedTime) => {
    if (!elapsedTime) return '-- min -- sec';
    const parts = elapsedTime.split(':');
    return `${parts[0] || '--'} min ${parts[1] || '--'} sec`;
  };

  // Mobile Card Component
  const MobileInvoiceCard = ({ item, index }) => (
    <Card 
      sx={{ 
        mb: 2, 
        position: 'relative',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        borderRadius: '12px',
        overflow: 'visible'
      }}
    >
      {item.flatbedNeeded && (
        <Chip
          label="Flatbed Requested"
          sx={{
            position: 'absolute',
            top: -8,
            left: 16,
            backgroundColor: '#ff7043',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.75rem',
            zIndex: 1
          }}
        />
      )}
      
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              #{item.invoiceId}
            </Typography>
            <Chip 
              label={item.callStatus || 'Pending'} 
              size="small"
              sx={{ 
                backgroundColor: getStatusColor(item.callStatus),
                color: 'white',
                fontWeight: 'bold',
                mt: 0.5
              }}
            />
          </Box>
          <IconButton 
            onClick={() => handleInvoiceActions(item, index)}
            sx={{ color: '#666' }}
          >
            <InfoIcon />
          </IconButton>
        </Box>

        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon sx={{ fontSize: 20, color: '#666' }} />
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {formatElapsedTime(item.elapsedTime)}
            </Typography>
          </Box>

          {item.etaPick && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShippingIcon sx={{ fontSize: 20, color: '#4caf50' }} />
              <Typography variant="body2" color="text.secondary">
                ETA: {item.etaPick}
              </Typography>
            </Box>
          )}

          {item.pickup && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <LocationOnIcon sx={{ fontSize: 20, color: '#f44336', mt: 0.2 }} />
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {item.pickup}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
        <Button
          size="small"
          onClick={() => showTrackingDetails(index)}
          sx={{ color: '#1976d2', fontWeight: 'bold' }}
        >
          Track Order
        </Button>
        <Button
          size="small"
          startIcon={<MapIcon />}
          onClick={() => handleRouteMapOpen(index)}
          sx={{ color: '#4caf50', fontWeight: 'bold' }}
        >
          View Map
        </Button>
      </CardActions>
    </Card>
  );

  // Desktop Row Component (existing layout)
  const DesktopInvoiceRow = ({ item, index }) => (
    <Grid item xs={12} key={index}>
      {!item.hookedTime && (
        <Paper style={{ position: 'relative', minWidth: 600 }}>
          {item.flatbedNeeded && (
            <div className="flatbed-section" style={{
              top: '0',
              left: '0',
              width: '100%',
              backgroundColor: '#ff7043',
              color: '#fff',
              textAlign: 'center',
              borderRadius: '30px 30px 0 0',
              fontSize: '14px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
            }}>
              <strong>Flatbed Requested</strong>
            </div>
          )}
          <Grid container item xs={12} alignItems="center" justifyContent="center">
            <Grid item xs={2} container justifyContent="center" onClick={() => showTrackingDetails(index)}>
              <Typography variant="body2" style={{ wordBreak: 'break-all' }}>#{item.invoiceId}</Typography>
            </Grid>
            <Grid item xs={4} container justifyContent="center" onClick={() => showTrackingDetails(index)}>
              <Typography variant="body2" style={{ textAlign: 'center' }}>
                {formatElapsedTime(item.elapsedTime)}
              </Typography>
            </Grid>
            <Grid item xs={3} container justifyContent="center">
              <Typography variant="body2" style={{ textAlign: 'center' }}>{item.etaPick}</Typography>
            </Grid>
            <Grid container justifyContent="center" item xs={3}>
              <Grid container justifyContent="flex-end">
                {((item.deviceId === getSessionData("uniqueDeviceId")) || (item.deviceId === localStorage.getItem('fcm_token'))) && (
                  <IconButton
                    style={{ padding: 0 }}
                    onClick={() => {
                      handleOpen();
                      setInvoiceToCancel(item.invoiceId);
                    }}>
                    <CancelIcon />
                  </IconButton>
                )}
                <IconButton style={{ padding: 0 }} onClick={() => handleRouteMapOpen(index)}>
                  <MapIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
          
          {item.pickup && (
            <Grid container item xs={12} style={{ 
              borderTop: '1px dashed #ccc',
              backgroundColor: '#f8f8f8',
              padding: '4px 8px',
              marginTop: '4px'
            }}>
              <Typography 
                variant="caption" 
                style={{ 
                  fontStyle: 'italic',
                  color: '#555',
                  fontSize: '0.85rem',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  width: '100%'
                }}
              >
                <span style={{ fontWeight: 'bold' }}>Pickup:</span> {item.pickup}
              </Typography>
            </Grid>
          )}
        </Paper>
      )}
    </Grid>
  );

  return (
    <>
      <Box sx={{ 
        flexGrow: 1, 
        p: isMobile ? 1 : 2,
        backgroundColor: "#f5f5f5",
        minHeight: '100vh'
      }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: isMobile ? 2 : 3,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            sx={{ 
              fontWeight: 'bold',
              color: '#1976d2',
              mb: isMobile ? 1 : 0
            }}
          >
            Track Orders
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto' }}>
            <Button 
              variant="contained" 
              color="success" 
              onClick={gotoHome}
              fullWidth={isMobile}
              startIcon={<AddIcon />}
              sx={{ fontWeight: 'bold' }}
            >
              NEW REQUEST
            </Button>
            <Button 
              variant="contained" 
              onClick={clearFromTrack} 
              color="primary"
              disabled={isRefreshing}
              fullWidth={isMobile}
              startIcon={isRefreshing ? null : <RefreshIcon />}
              sx={{ fontWeight: 'bold' }}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
        </Box>

        {/* Loading State */}
        {isRefreshing && (
          <Box sx={{ mb: 2 }}>
            {[1, 2, 3].map((item) => (
              <Skeleton
                key={item}
                variant="rectangular"
                height={isMobile ? 120 : 60}
                sx={{ mb: 1, borderRadius: '8px' }}
              />
            ))}
          </Box>
        )}

        {/* Alert */}
        {showAlert && (
          <Alert 
            severity={alertSeverity} 
            sx={{ 
              position: 'fixed',
              top: isMobile ? 20 : 80,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              minWidth: isMobile ? '90%' : '300px',
              maxWidth: isMobile ? '90%' : '500px'
            }}
          >
            {alertMessage}
          </Alert>
        )}

        {/* Content */}
        {isMobile ? (
          // Mobile Layout
          <Box>
            {initialRespData.length === 0 && !isRefreshing ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No active orders found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Pull down to refresh or create a new request
                </Typography>
              </Box>
            ) : (
              (Array.isArray(initialRespData) ? initialRespData : [])
                .filter(item => !item.hookedTime)
                .map((item, index) => (
                  <MobileInvoiceCard key={index} item={item} index={index} />
                ))
            )}
          </Box>
        ) : (
          // Desktop Layout
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Grid container item xs={12} alignItems="center" justifyContent="center" style={{ minWidth: 600 }}>
              <Grid item xs={2} container justifyContent="center">
                <Typography variant="body2" component="div" style={{ fontWeight: 'bold' }}>Invoice</Typography>
              </Grid>
              <Grid item xs={4} container justifyContent="center">
                <Typography variant="body2" component="div" style={{ fontWeight: 'bold', textAlign: 'center' }}>Time Elapsed</Typography>
              </Grid>
              <Grid item xs={3} container justifyContent="center">
                <Typography variant="body2" component="div" style={{ fontWeight: 'bold', textAlign: 'center' }}>ETA</Typography>
              </Grid>
              <Grid item xs={3} container justifyContent="center">
                <Typography variant="body2" component="div" style={{ fontWeight: 'bold', textAlign: 'right' }}>Actions</Typography>
              </Grid>
            </Grid>
            {(Array.isArray(initialRespData) ? initialRespData : []).map((item, index) => (
              <DesktopInvoiceRow key={index} item={item} index={index} />
            ))}
          </Box>
        )}

        {/* Floating Action Button for Mobile */}
        {isMobile && (
          <Fab
            color="primary"
            aria-label="refresh"
            onClick={clearFromTrack}
            disabled={isRefreshing}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: 1000
            }}
          >
            <RefreshIcon />
          </Fab>
        )}
      </Box>

      {/* Action Drawer for Mobile */}
      <SwipeableDrawer
        anchor="bottom"
        open={openActionDrawer}
        onClose={() => setOpenActionDrawer(false)}
        onOpen={() => setOpenActionDrawer(true)}
        sx={{ display: isMobile ? 'block' : 'none' }}
      >
        <Box sx={{ p: 2, minHeight: 200 }}>
          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
            Invoice Actions
          </Typography>
          {selectedInvoiceForActions && (
            <>
              <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
                Invoice #{selectedInvoiceForActions.item.invoiceId}
              </Typography>
              <List>
                <ListItem button onClick={() => {
                  showTrackingDetails(selectedInvoiceForActions.index);
                  setOpenActionDrawer(false);
                }}>
                  <ListItemIcon>
                    <InfoIcon />
                  </ListItemIcon>
                  <ListItemText primary="View Details" />
                </ListItem>
                
                <ListItem button onClick={() => {
                  handleRouteMapOpen(selectedInvoiceForActions.index);
                  setOpenActionDrawer(false);
                }}>
                  <ListItemIcon>
                    <MapIcon />
                  </ListItemIcon>
                  <ListItemText primary="View on Map" />
                </ListItem>

                {((selectedInvoiceForActions.item.deviceId === getSessionData("uniqueDeviceId")) || 
                  (selectedInvoiceForActions.item.deviceId === localStorage.getItem('fcm_token'))) && (
                  <ListItem button onClick={() => {
                    handleOpen();
                    setInvoiceToCancel(selectedInvoiceForActions.item.invoiceId);
                    setOpenActionDrawer(false);
                  }}>
                    <ListItemIcon>
                      <CancelIcon sx={{ color: '#f44336' }} />
                    </ListItemIcon>
                    <ListItemText primary="Cancel Order" primaryTypographyProps={{ color: '#f44336' }} />
                  </ListItem>
                )}
              </List>
            </>
          )}
        </Box>
      </SwipeableDrawer>

      {/* Cancel Modal */}
      <Modal
        open={isOpenCancelWindow}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '90%' : '90%',
          maxWidth: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: isMobile ? 2 : 4,
          borderRadius: '12px'
        }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Cancel Reason
          </Typography>
          <TextField
            id="outlined-basic"
            label="Reason"
            variant="outlined"
            fullWidth
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            sx={{ mb: 2 }}
          />
          {!cancelReason && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              Please provide a reason for cancellation.
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => { handleClose(); setCancelReason(""); }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={!cancelReason}
              onClick={() => {
                if (cancelReason) {
                  cancelInvoice();
                }
              }}
            >
              Confirm Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

  <Modal
  open={openRouteMap}
  onClose={() => setRouteMap(false)}
  aria-labelledby="modal-modal-title"
  aria-describedby="modal-modal-description"
>
  <div style={modalStyle}>
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      marginBottom: '10px', 
      backgroundColor: '#333', 
      padding: isMobile ? '8px' : '10px',
      borderRadius: isMobile ? '12px 12px 0 0' : '8px 8px 0 0'
    }}>
      <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
        margin: '0', 
        color: '#fff', 
        textAlign: 'center', 
        flexGrow: 1,
        fontWeight: 'bold' 
      }}>
        ARS Enroute Map
      </Typography>
      <IconButton
        onClick={() => setRouteMap(false)}
        sx={{ color: '#f21c0c' }}
      >
        <CloseIcon />
      </IconButton>
    </div>
    <div style={{ 
      height: 'calc(100% - 60px)', // Subtract header height
      overflow: 'hidden',
      position: 'relative'
    }}>
      <RouteMap 
        mapType={mapType} 
        data={routeCoordinates} 
        isModal={true}
        zoom={10}
      />
    </div>
  </div>
</Modal>

      {/* Tracking Modal */}
      <Modal
        open={openTrackingModal}
        onClose={() => setOpenTrackingModal(false)}
      >
        <div style={modalStyle}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '10px', 
            backgroundColor: '#f21c0c', 
            padding: isMobile ? '8px' : '10px' 
          }}>
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
              margin: '0', 
              color: '#fff', 
              textAlign: 'center', 
              flexGrow: 1,
              fontWeight: 'bold' 
            }}>
              ARS Invoice
            </Typography>
            <IconButton
              onClick={() => setOpenTrackingModal(false)}
              sx={{ color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </div>
          {requestDetails && (
            <TrackingComponent
              invoiceId={requestDetails.invoiceId}
              clearFromTrack={clearFromTrack}
              hookedTime={requestDetails.hookedTime}
              onRevertCall={() => {
                fetchInitialData();
                setShowTrackingDetailsID(null);
                setOpenTrackingModal(false);
              }}
              cancelReason={(requestDetails.callStatus === 'Cancel') ? requestDetails.reason : ''}
              invoiceStatus={requestDetails.callStatus}
              steps={trackingData}
              isLoading={isLoading}
              data={requestDetails}
            />
          )}
        </div>
      </Modal>
    </>
  );
}

export default TrackOrder;